import { isDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import type { Color } from '@/components/sg-design-system/types/colors.ts';

interface LiveUpdatableInputProps {
  className?: string;
  'data-e2e'?: string;
  liveValue: number | undefined;
  editValue: number | undefined;
  onChange: (value: number | undefined | null) => void;
  unit?: string;
  unitBackgroundColor?: Color;
  onUnitClick?: () => void;
}

export function LiveUpdatableInput({
  className,
  liveValue,
  editValue,
  onChange,
  unit,
  onUnitClick,
  unitBackgroundColor,
  'data-e2e': dataE2e,
}: LiveUpdatableInputProps) {
  const { locale } = useIntl();
  const [mode, setMode] = useState<'edit' | 'display'>('display');
  const [tempValue, setTempValue] = useState<number | undefined>(undefined);

  function edit(currentValue: number | undefined) {
    setTempValue(currentValue);
    setMode('edit');
  }

  function display() {
    setTempValue(undefined);
    setMode('display');
  }

  function handleChange(value: number | null | undefined) {
    onChange(value);
    display();
  }

  const isPinned = isDefined(editValue);
  const currentValue = editValue ?? liveValue;

  return (
    <div className="d-flex flex-grow-1">
      {
        <SgNumberInput
          size="sm"
          data-e2e={dataE2e}
          value={mode === 'display' ? currentValue : (tempValue ?? editValue)}
          unit={unit}
          onFocus={() => edit(currentValue)}
          onBlur={() => display()}
          onChange={handleChange}
          onUnitClick={onUnitClick}
          unitBackgroundColor={unitBackgroundColor}
          className={isPinned ? 'text-info fw-bold' : className}
          valuePosition="start"
          unitPosition="end"
          locale={locale}
          selectAllOnFocus
          acceleratorsEnabled={isDefined(unit)}
        />
      }
      {isPinned && (
        <Tooltip contents={<FormattedMessage id="option.pin.tooltip.message" />}>
          <Button onClick={() => onChange(null)} data-e2e="pin-button" size="sm" purpose="flat" iconPosition="start">
            <Icon icon="push_pin" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
